import { PlanCurrency, annualDiscountPercent, formatPlanPrice, isKnownPlanCountry, planCurrencyForCountry, publishedSeatPrice } from 'constants/subscription'
import { useCallback, useEffect, useMemo } from 'react'

import { SubscriptionPeriod } from 'models/user/userWorkspaceDTOs'
import { logger } from 'utils/logging'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

interface PlanPricing {
  /** Currency this user's checkout will bill in */
  currency: PlanCurrency
  /** Price of one Pro seat per month in that currency, or null when none is published for it */
  seatMonthlyPrice: (period: SubscriptionPeriod) => number | null
  /** Saving the yearly period offers in that currency, or null when it cannot be quoted */
  annualDiscount: number | null
  /** Formats an amount in that currency, in the language the app is displayed in */
  formatAmount: (amount: number) => string
}

/** Countries already reported, so an unusable one is reported once per session rather than per render. */
const reportedCountries = new Set<string>()

/**
 * Plan pricing for the signed-in user: the currency their checkout bills in, and the Pro seat price
 * in it. Every surface that shows a plan figure reads it from here, so one view can only ever
 * render one currency.
 */
export function usePlanPricing(): PlanPricing {
  const { baseUserData } = useUserData()
  const { i18n } = useTranslation()

  const country = baseUserData?.country
  const currency = useMemo(() => planCurrencyForCountry(country), [country])

  // A country the app cannot place still resolves to euro, which is the right thing to render but
  // the wrong thing to do silently: the customer would be quoted a currency their checkout may not
  // bill in. Reported only once /user/me has answered, so a pending load is not mistaken for one.
  useEffect(() => {
    if (!baseUserData || isKnownPlanCountry(country)) return
    const reportKey = country ?? '<none>'
    if (reportedCountries.has(reportKey)) return

    reportedCountries.add(reportKey)
    logger.warn('Quoting plan prices in the default currency for an unrecognised country', undefined, {
      context: {
        component: 'usePlanPricing',
        action: 'planCurrencyForCountry',
        metadata: { country: reportKey, currency },
      },
    })
  }, [baseUserData, country, currency])

  const seatMonthlyPrice = useCallback(
    (period: SubscriptionPeriod) => publishedSeatPrice(currency, period),
    [currency]
  )

  const annualDiscount = useMemo(() => annualDiscountPercent(currency), [currency])

  const formatAmount = useCallback(
    (amount: number) => formatPlanPrice(amount, currency, i18n.language),
    [currency, i18n.language]
  )

  return { currency, seatMonthlyPrice, annualDiscount, formatAmount }
}
